import { motion } from 'framer-motion';
import { Workflow, Sparkles, Target } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { FeatureCard } from '@/components/ui/grid-feature-cards';
const painPoints = [{
  icon: Workflow,
  title: "Broken Marketing Workflows",
  description: <>Offline? You're a rockstar. But your online process is fragmented with nothing connecting the dots.<br /><br /><strong>You're stuck coordinating chaos instead of creating magic.</strong></>,
  color: "bg-gradient-to-br from-red-500 to-orange-500"
}, {
  icon: Sparkles,
  title: "Scattered AI Tools",
  description: <>Five AI subscriptions, eight logins (eight forgotten passwords), and <strong>zero tools that actually understand your brand.</strong><br /><br />Every project means re-training, re-explaining, re-frustrating yourself.</>,
  color: "bg-gradient-to-br from-purple-500 to-violet-500"
}, {
  icon: Target,
  title: "Pressure to Deliver Results",
  description: <>Your team is measured on campaigns shipped and ROI delivered, not on how well you've mastered the latest AI tool.<br /><br /><strong>You need results, not another learning curve.</strong></>,
  color: "bg-gradient-to-br from-green-500 to-emerald-500"
}];
const floatingPills = [{
  title: "Security & Privacy",
  className: "top-8 left-4 lg:top-10 lg:left-[15%] xl:left-[20%]",
  color: "text-destructive"
}, {
  title: "Brand Inconsistency",
  className: "top-24 left-0 lg:top-24 lg:left-[10%] xl:left-[15%]",
  color: "text-purple-600"
}, {
  title: "No Collaboration",
  className: "top-40 left-4 lg:top-40 lg:left-[12%] xl:left-[18%]",
  color: "text-amber-600"
}, {
  title: "Constantly Changing Landscape",
  className: "top-8 right-4 lg:top-10 lg:right-[15%] xl:right-[20%]",
  color: "text-blue-600"
}, {
  title: "Prompt Frustration",
  className: "top-24 right-0 lg:top-24 lg:right-[10%] xl:right-[15%]",
  color: "text-emerald-600"
}, {
  title: "Last Mile Challenges",
  className: "top-40 right-4 lg:top-40 lg:right-[12%] xl:right-[18%]",
  color: "text-rose-500"
}];
const leftPills = floatingPills.slice(0, 3);
const rightPills = floatingPills.slice(3, 6);
const MessyMiddleSection = () => {
  return <section className="py-12 sm:py-16 lg:py-20 bg-muted/30 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header with floating pills on sides */}
        <div className="flex items-start justify-center gap-4 lg:gap-8 mb-8 lg:mb-12">
          
          {/* Left pills column */}
          <div className="hidden lg:flex flex-col gap-4 pt-12 w-48 items-end">
            {leftPills.map((pill, index) => <motion.div key={pill.title} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }}>
                <motion.div animate={{
              y: [0, -6 + index % 3 * 2, 0]
            }} transition={{
              duration: 3.5 + index * 0.4,
              repeat: Infinity,
              ease: "easeInOut",
              delay: index * 0.15
            }} className="relative">
                  <div className="bg-card rounded-xl shadow-md px-3 py-2 border border-border/50 backdrop-blur-sm">
                    <p className={`${pill.color} font-semibold text-xs whitespace-nowrap`}>{pill.title}</p>
                  </div>
                  <div className="absolute -bottom-1.5 right-4 w-3 h-3 bg-card border-r border-b border-border/50 transform rotate-45" />
                </motion.div>
              </motion.div>)}
          </div>

          {/* Center content */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-center max-w-2xl">
            <Badge variant="outline" className="mb-3">
              The Messy Middle
            </Badge>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-3">
              You Already Know the <span className="bg-gradient-primary bg-clip-text text-transparent">Struggle</span>
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground">
              You're a seasoned marketer expected to move faster, create more, and somehow stay on-brand while juggling 12 tools, 47 deadlines, and a brief that changed three times today.
            </p>
            <p className="text-base sm:text-lg mt-3">
              <span className="bg-gradient-primary bg-clip-text text-transparent font-semibold">Wasn't AI supposed to help with all of this?</span>
            </p>
          </motion.div>

          {/* Right pills column */}
          <div className="hidden lg:flex flex-col gap-4 pt-12 w-48 items-start">
            {rightPills.map((pill, index) => <motion.div key={pill.title} initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }}>
                <motion.div animate={{
              y: [0, -6 + index % 3 * 2, 0]
            }} transition={{
              duration: 3.5 + index * 0.4,
              repeat: Infinity,
              ease: "easeInOut",
              delay: index * 0.15
            }} className="relative">
                  <div className="bg-card rounded-xl shadow-md px-3 py-2 border border-border/50 backdrop-blur-sm">
                    <p className={`${pill.color} font-semibold text-xs whitespace-nowrap`}>{pill.title}</p>
                  </div>
                  <div className="absolute -bottom-1.5 left-4 w-3 h-3 bg-card border-r border-b border-border/50 transform rotate-45" />
                </motion.div>
              </motion.div>)}
          </div>
        </div>

        {/* Cards Grid - 3 columns on desktop */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6 max-w-5xl mx-auto">
          {painPoints.map((painPoint, index) => <motion.div key={painPoint.title} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: index * 0.1
        }}>
              <FeatureCard feature={painPoint} className="h-full" compact />
            </motion.div>)}
        </div>
      </div>
    </section>;
};
export default MessyMiddleSection;
